/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { accessConfigService } from "~/services";
import { AccessConfig, AccessConfigTemplate, DefaultRadioManager, RadioManager } from "~/types/accessConfig";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

// Define the state type for the slice
interface AccessConfigState {
  loading: boolean;
  defaultRadioManagerLoading: boolean;
  data: AccessConfig[];
  radioManagerData: RadioManager[];
  templates: AccessConfigTemplate[];
  current: AccessConfig | null;
  defaultRadioManagerCurrent: DefaultRadioManager | null;
  open: boolean;
  defaultRadioManagerOpen: boolean;
  isEditing: boolean;
  defaultRadioManager: DefaultRadioManager[];
}

// Initial state
const initialState: AccessConfigState = {
  loading: true,
  defaultRadioManagerLoading: true,
  data: [],
  radioManagerData: [],
  templates: [],
  current: null,
  defaultRadioManagerCurrent: null,
  open: false,
  defaultRadioManagerOpen: false,
  isEditing: false,
  defaultRadioManager: [],
};

// Async thunks for API calls
export const fetchAccessConfigs = createAsyncThunk(
  "accessConfig/fetchAccessConfigs",
  async () => {
    try {
      const data = await accessConfigService.getAccessConfigData({ filter: { target: "DEVICE" } });
      return data as unknown as AccessConfig[];
    } catch (error) {
      console.error("Error fetching access config data:", error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; access config is admin-only and not surfaced in KPI UI
  { condition: () => !isPartnerKpiOnlyUser() },
);

export const fetchRadioManagerData = createAsyncThunk(
  "accessConfig/fetchRadioManagerData",
  async () => {
    try {
      const data = await accessConfigService.getAccessConfigData({ filter: { target: "RADIO" } });
      return data as unknown as RadioManager[];
    } catch (error) {
      console.error("Error fetching radio manager data:", error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; Radio Manager row in RadioSummary is operator-only
  { condition: () => !isPartnerKpiOnlyUser() },
);

export const fetchAccessConfigTemplates = createAsyncThunk(
  "accessConfig/fetchAccessConfigTemplates",
  async () => {
    try {
      const templates = await accessConfigService.getAccessConfigTemplate();
      return templates;
    } catch (error) {
      console.error("Error fetching access config templates:", error);
      throw error;
    }
  }
);

export const fetchDefaultRadioManager = createAsyncThunk(
  "accessConfig/fetchDefaultRadioManager",
  async () => {
    try {
      const data = await accessConfigService.getDefaultRadioManagerData();
      return data;
    } catch (error) {
      console.error("Error fetching default radio manager:", error);
      throw error;
    }
  }
);


// Create the slice
const accessConfigSlice = createSlice({
  name: "accessConfig",
  initialState,
  reducers: {
    setSelectedConfig: (state, action: PayloadAction<AccessConfig | null>) => {
      state.current = action.payload;
    },
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDefaultRadioManagerDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.defaultRadioManagerOpen = action.payload;
    },
    setDefaultRadioManagerSelectedConfig: (state, action: PayloadAction<DefaultRadioManager | null>) => {
      state.defaultRadioManagerCurrent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccessConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      // MCS-3868: clear loading on failure so the Access Config tab can't spin forever on a 403.
      .addCase(fetchAccessConfigs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchRadioManagerData.fulfilled, (state, action) => {
        state.radioManagerData = action.payload;
      })
      .addCase(fetchAccessConfigTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(fetchDefaultRadioManager.fulfilled, (state, action) => {
        state.defaultRadioManagerLoading = false;
        state.defaultRadioManager = action.payload;
      })
  },
});

// Export actions
export const accessConfigActions = accessConfigSlice.actions;

// Export selectors
export const accessConfigData = (state: RootState) => state.accessConfig.data;
export const radioManagerData = (state: RootState) => state.accessConfig.radioManagerData;
export const accessConfigTemplates = (state: RootState) => state.accessConfig.templates;
export const accessConfigState = (state: RootState) => state.accessConfig.current;
export const accessConfigLoading = (state: RootState) => state.accessConfig.loading;
export const accessConfigOpen = (state: RootState) => state.accessConfig.open;
export const accessConfigIsEditing = (state: RootState) => state.accessConfig.isEditing;
export const defaultRadioManagerData = (state: RootState) => state.accessConfig.defaultRadioManager;
export const defaultRadioManagerLoading = (state: RootState) => state.accessConfig.defaultRadioManagerLoading;
export const defaultRadioManagerOpen = (state: RootState) => state.accessConfig.defaultRadioManagerOpen;
export const defaultRadioManagerCurrent = (state: RootState) => state.accessConfig.defaultRadioManagerCurrent;
// Export reducer
export default accessConfigSlice.reducer;
